import type { HypothesisPhase } from '../types';

/**
 * B spec §5.3 — **the Coach Claude response validator** (build unit B-15).
 *
 * The response crosses a trust boundary twice: a model wrote it and a Worker relayed it. §5.3
 * makes the client's posture explicit — *"per-entry and conservative"* — and this module is that
 * posture as code: it takes `unknown`, keeps only entries it can fully account for, and answers
 * with a list. **Zero surviving entries is not an error here**; it is the `badResponse` arm of
 * §5.4's taxonomy, which the caller renders. A validator that threw would turn a garbled answer
 * into a crash on the one surface whose whole promise is *"the manual path always works"*.
 *
 * ── Why this is pure, and in `domain/` ──────────────────────────────────────
 *
 * It decides; it performs no I/O and reads no clock. `app/coach.ts` owns the fetch and the
 * §5.4 mapping; the workflow owns what the surviving entries become (`AdjustmentEntry` needs a
 * minted id and a timestamp, neither of which a pure module may produce). Splitting it here is
 * what lets the Node suite drive every drop rule against real garbage.
 */

/** One surviving suggestion — §5.3's response entry, after validation. */
export interface CoachSuggestion {
  readonly text: string;
  readonly phase: HypothesisPhase;
  /** Only ids that were in the request's selection survive; unknown ids are filtered out. */
  readonly sourceHypothesisIds: readonly string[];
}

/** §4.3's two bounds, passed in rather than imported — `domain/` takes its limits as arguments. */
export interface SuggestionBounds {
  /** `COACH_MAX_SUGGESTIONS` — the most entries one response may contribute. */
  readonly maxSuggestions: number;
  /** `COACH_ADJUSTMENT_MAX_CHARS` — the per-entry text bound `ERR_CARD_TOO_LARGE` also enforces. */
  readonly maxChars: number;
}

/**
 * The seven `HypothesisPhase` values as a membership set.
 *
 * A `Record` over the union rather than a list, so a phase added to the enum and not to this
 * object is a **compile error** — the failure with no runtime symptom otherwise being that an
 * entry on the new phase is silently dropped as invalid, which reads to the user as Coach Claude
 * ignoring a pattern. (`ui/halftime/phaseOptions.ts` declares the same union for a different
 * job — a *picker* with labels and reading order — and `domain/` may not import `ui/`. Logged as
 * a B-20 duplication-ledger candidate: same union, two jobs, both compile-closed.)
 */
const PHASE_MEMBERS: Record<HypothesisPhase, true> = {
  inPossession: true,
  outOfPossession: true,
  attTransition: true,
  defTransition: true,
  setPiece: true,
  individual: true,
  gk: true,
};

/** §5.3 — is this one of the seven phases? Anything else is a dropped entry, not a coercion. */
export function isHypothesisPhase(value: unknown): value is HypothesisPhase {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(PHASE_MEMBERS, value);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * §5.3's per-entry validation, in one pass.
 *
 * Drop rules, each one because the alternative is worse than the drop:
 *
 * | Dropped when | Because |
 * |---|---|
 * | the body is not `{ suggestions: [...] }` | there is nothing to read; the caller reports `badResponse` |
 * | `text` is missing, not a string, or blank after trim | a blank page on a card is scenario 29's failure |
 * | `text` exceeds `maxChars` | `UpsertCoachCardDraft` would reject the whole draft (`ERR_CARD_TOO_LARGE`) — one over-long suggestion must not cost the user the other seven |
 * | `phase` is not one of the seven | the card names the phase an adjustment applies to; an entry with no honest phase has no page to render on |
 *
 * `sourceHypothesisIds` is **filtered, not fatal**: an entry whose "because —" cites one real
 * pattern and one hallucinated id is still a usable adjustment about the real one, and
 * `ERR_CARD_SOURCE` would otherwise reject the whole draft. An entry left citing nothing is kept
 * — a manual entry may cite nothing either (§2.5 step 3), so the card can already render it.
 */
export function validateCoachSuggestions(
  payload: unknown,
  selectedHypothesisIds: readonly string[],
  bounds: SuggestionBounds,
): CoachSuggestion[] {
  const body = asRecord(payload);
  if (body === null) return [];
  const raw = body.suggestions;
  if (!Array.isArray(raw)) return [];

  const allowed = new Set(selectedHypothesisIds);
  const kept: CoachSuggestion[] = [];

  for (const candidate of raw) {
    if (kept.length >= bounds.maxSuggestions) break;
    const entry = asRecord(candidate);
    if (entry === null) continue;

    const text = typeof entry.text === 'string' ? entry.text.trim() : '';
    if (text === '' || text.length > bounds.maxChars) continue;

    const phase = entry.phase;
    if (!isHypothesisPhase(phase)) continue;

    const sources = Array.isArray(entry.sourceHypothesisIds)
      ? entry.sourceHypothesisIds.filter(
          (id): id is string => typeof id === 'string' && allowed.has(id),
        )
      : [];

    kept.push({ text, phase, sourceHypothesisIds: sources });
  }

  return kept;
}

/**
 * B-30 / §20 — **how many entries the model actually returned**, before any of the rules above.
 *
 * The `break` in {@link validateCoachSuggestions} truncates at `maxSuggestions` **silently**, and
 * once §3.8.43 stopped the Worker telling the model any number, a response longer than the card is
 * a thing that can now happen. Silently trimming the menu is precisely the failure §3.8.43 exists
 * to eliminate (*"i want to order from a bigger, better menu"*), so the review step says it once
 * when it bites — and to say *"Coach Claude returned 24"* the surface needs the 24.
 *
 * It reads the same body shape the validator does, in the same module, so the two cannot come to
 * disagree about what "returned" counts. A body that is not `{ suggestions: [...] }` returned
 * nothing: zero, never a throw — the caller is already reporting `badResponse` on that path.
 */
export function countReturnedSuggestions(payload: unknown): number {
  const body = asRecord(payload);
  if (body === null) return 0;
  return Array.isArray(body.suggestions) ? body.suggestions.length : 0;
}
